import { FC, Fragment } from 'react';
import FleetTitle from '@/libs/antd/components/Title';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import DataRow from '@/libs/antd/components/DataRow';
import {
	Button,
	Col,
	Divider,
	Form,
	notification,
	Row,
	Select,
	Table,
} from 'antd';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import TextArea from 'antd/es/input/TextArea';
import { useModals } from '@/components/modals/ModalProvider';
import { useApp } from '@/App';
import {
	getCustomerDetail,
	verifyCustomer,
} from '@/libs/fleet-api/services/customer';
import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import { useForm } from '@/libs/antd/form';
import { useSelectRejectCustomer } from '@/libs/fleet-api/hooks/master-data';
import rule from '@/libs/antd/rules';
import { DocumentItem } from '@/components/modals/DocumentModal';

type CustomerVerificationForm = {
	verificationStatus: 1 | 0 | -1;
	rejectReason: number | null;
	verificationNotes: string;
};

const FSCustomerPage: FC<{ customerId: string }> = ({ customerId }) => {
	const { navigate } = useApp();
	const {
		modal,
		openPreviewModal,
		openCustomerGroupDetail,
		openDocumentModal,
		openCustomerDetail,
	} = useModals();

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getCustomerDetail', customerId] as const,
		queryFn: ({ queryKey }) => {
			const [, customerId] = queryKey;
			return getCustomerDetail(customerId);
		},
		enabled: !!customerId,
	});

	const { form, value, fieldName } = useForm<CustomerVerificationForm>();

	const submitVerifyCustomer = useMutation({ mutationFn: verifyCustomer });

	const selectRejectCustomer = useSelectRejectCustomer();

	const {
		customerInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#customer-detail"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(customerId);
								}}
							>
								{customerInformation?.customerName}
							</a>,
						],
						['Customer ID', customerId],
					],
					[
						[
							'Kelas Industri',
							customerInformation?.industrySector.industryClass,
						],
						['Sumber Order', null],
					],
					[
						[
							'Portofolio Guideline',
							customerInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							customerInformation?.customerGroup?.id ? (
								<a
									href="#customer-detail"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										if (customerInformation?.customerGroup?.id) {
											openCustomerGroupDetail(
												customerInformation?.customerGroup?.id,
											);
										}
									}}
									aria-disabled={!customerInformation?.customerGroup?.id}
								>
									{customerInformation?.customerGroup?.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								key="view-dokumen"
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
						['Divisi', customerInformation?.division ?? '-'],
					],
					[
						['Nama FBDH', customerInformation?.fbdh?.value],
						['Nama FRM', customerInformation?.frm?.value],
					],
				]}
				style={{
					marginBottom: 20,
				}}
			/>

			<FleetTitle>Data Legalitas</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				dataSource={
					customerLegalDocuments?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;
										openPreviewModal(record.file as FleetFile);
									}}
									style={!record.file?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Jenis Dokumen',
						width: 600,
						dataIndex: ['documentType'],
					},
					{
						title: 'No Dokumen',
						dataIndex: ['documentNo'],
						render: (_, record) => {
							return record.documentNo && record.defaultDocumentNo
								? record.documentNo
								: record.defaultDocumentNo
									? record.defaultDocumentNo
									: '';
						},
					},
					{
						title: 'Tanggal Dokumen',
						dataIndex: ['documentDate'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<FleetTitle>Data Perizinan Usaha</FleetTitle>

			<Table
				dataSource={
					customerBusinessDocuments?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'View Detail',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!record.file?.id) return;

										openPreviewModal({
											id: record.file.id as unknown as number,
											name: record.file.name,
											url: record.file.url,
										});
									}}
									style={!record.file?.id ? { color: '#bababa' } : {}}
								>
									<FileSearchOutlined />
								</a>
							);
						},
					},
					{
						title: 'Jenis Dokumen',
						dataIndex: ['documentType'],
					},
					{
						title: 'No Dokumen',
						dataIndex: ['documentNo'],
					},
					{
						title: 'Penerbit',
						dataIndex: ['publisher', 'value'],
					},
					{
						title: 'Tanggal Dokumen',
						dataIndex: ['documentDate'],
					},
					{
						title: 'Masa Berlaku',
						dataIndex: ['validityPeriod', 'value'],
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			{customerReferralDocuments && customerReferralDocuments.length > 0 && (
				<>
					<FleetTitle>Data Referral JF</FleetTitle>
					<Table
						scroll={{ x: 'max-content' }}
						dataSource={
							customerReferralDocuments?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'View Detail',
								align: 'center',
								render: (_, record) => {
									return (
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault();
												if (!record.file?.id) return;
												openPreviewModal(record.file as FleetFile);
											}}
											style={!record.file?.id ? { color: '#bababa' } : {}}
										>
											<FileSearchOutlined />
										</a>
									);
								},
							},
							{
								title: 'Jenis Dokumen',
								dataIndex: ['documentType'],
							},
							{
								title: 'No Dokumen',
								dataIndex: ['documentNo'],
							},
							{
								title: 'Tanggal Dokumen',
								dataIndex: ['documentDate'],
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
					<Divider />
				</>
			)}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={{
					verificationStatus: undefined,
					rejectReason: null,
					verificationNotes: '',
				}}
				onFinish={async (values) => {
					try {
						// setSuccessModal(false);
						const { data } = await submitVerifyCustomer.mutateAsync({
							customerId,
							verificationStatus: values.verificationStatus,
							rejectReason: values.rejectReason ?? undefined,
							verificationNotes: values.verificationNotes,
						});
						if (data) {
							await modal.success({
								title:
									values.verificationStatus === -1 ? (
										<Fragment>
											<>Sukses</>
											<small>Customer berhasil di reject</small>
										</Fragment>
									) : (
										<Fragment>
											<small>Customer ID Anda</small>
											<div style={{ fontSize: 30 }}>{customerId}</div>
										</Fragment>
									),
								subTitle:
									values.verificationStatus === -1
										? null
										: 'Terdapat masa aktif new order yaitu 30 hari, jika lebih dari 30 hari maka status order in akan expired.',
							});
							navigate('FSCustomerListPage');
						}
					} catch (error) {
						//
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages?.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li key={msg}>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				<Row gutter={[20, 40]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('verificationStatus')}
							label="Verification Status"
							rules={[rule.required()]}
						>
							<Select
								options={[
									{ value: 1, label: 'Verified' },
									{ value: 0, label: 'Return' },
									{ value: -1, label: 'Reject' },
								]}
							/>
						</Form.Item>
					</Col>
					{value?.verificationStatus === -1 && (
						<Col span={12}>
							<Form.Item
								name={fieldName('rejectReason')}
								label="Category Reject"
								rules={[rule.required()]}
							>
								<Select {...selectRejectCustomer} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
				<Row gutter={[20, 40]}>
					<Col span={24}>
						<Form.Item
							name={fieldName('verificationNotes')}
							label="Verification Notes"
							labelCol={{ span: 6, style: { marginLeft: -5 } }}
						>
							<TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Button
					type="primary"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 50,
					}}
					loading={isLoading}
					onClick={async () => {
						await form.validateFields();
						form.submit();
					}}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default FSCustomerPage;
